"use client"

/* eslint-disable functional/immutable-data -- ページ滞在中の計測済み深度を保持する */
import { usePathname } from "next/navigation"
import { useEffect, useRef } from "react"

import { trackEvent } from "@/lib/analytics"
import { linkEvents } from "@/lib/link-analytics"
import { pageService } from "@/lib/service-analytics"

export function SiteBehaviorTracker() {
  const pathname = usePathname()
  const lastViewedPath = useRef<string | null>(null)
  useEffect(() => {
    const service = pageService(pathname)
    if (lastViewedPath.current !== pathname) {
      lastViewedPath.current = pathname
      if (service)
        trackEvent("service_view", { service_id: service, page_path: pathname })
    }
    const depths = new Set<number>()
    let frame = 0
    const measure = () => {
      frame = 0
      const height = document.documentElement.scrollHeight
      if (height <= window.innerHeight) return
      const percent = ((window.scrollY + window.innerHeight) / height) * 100
      for (const depth of [25, 50, 75, 90]) {
        if (percent < depth || depths.has(depth)) continue
        depths.add(depth)
        trackEvent("scroll_depth", {
          page_path: pathname,
          service_id: service,
          percent_scrolled: depth,
        })
      }
    }
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(measure)
    }
    const onClick = (event: MouseEvent) => {
      if (!(event.target instanceof Element)) return
      const anchor = event.target.closest("a")
      if (!anchor) return
      const article = anchor.closest<HTMLElement>("[data-guide-slug]")
      const location =
        anchor.dataset.ctaLocation ??
        (anchor.closest("nav")
          ? "navigation"
          : anchor.closest("footer")
            ? "footer"
            : anchor.closest("[data-guide-service]")
              ? "guide_service"
              : "content")
      for (const tracked of linkEvents({
        href: anchor.href,
        origin: window.location.origin,
        pagePath: pathname,
        location,
        tracked: anchor.dataset.ctaTracked === "true",
        guideSlug: article?.dataset.guideSlug,
      })) {
        trackEvent(tracked.name, tracked.params)
      }
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    document.addEventListener("click", onClick)
    return () => {
      window.removeEventListener("scroll", onScroll)
      document.removeEventListener("click", onClick)
      window.cancelAnimationFrame(frame)
    }
  }, [pathname])
  return null
}
